import React, {useEffect, useMemo, useState} from 'react'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material'
import {Close as CloseIcon, OpenInNew as OpenInNewIcon} from '@mui/icons-material'
import Papa from 'papaparse'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type {ChatAttachment} from './types'
import {attachmentPreviewKind} from './attachmentPresentation'

const MAX_TEXT_PREVIEW_BYTES = 2 * 1024 * 1024
const MAX_CSV_ROWS = 200
const MAX_CSV_COLUMNS = 50

export interface CsvPreview {
  rows: string[][]
  truncatedRows: boolean
  truncatedColumns: boolean
  errors: string[]
}

export function parseCsvPreview(text: string, name: string): CsvPreview {
  const delimiter = name.toLowerCase().endsWith('.tsv') ? '\t' : undefined
  const parsed = Papa.parse<string[]>(text, {
    delimiter,
    preview: MAX_CSV_ROWS + 1,
    skipEmptyLines: 'greedy',
  })
  const sourceRows = parsed.data.map(row => row.map(value => String(value ?? '')))
  const truncatedRows = sourceRows.length > MAX_CSV_ROWS
  const rows = sourceRows.slice(0, MAX_CSV_ROWS)
  const truncatedColumns = rows.some(row => row.length > MAX_CSV_COLUMNS)
  return {
    rows: rows.map(row => row.slice(0, MAX_CSV_COLUMNS)),
    truncatedRows,
    truncatedColumns,
    errors: parsed.errors.slice(0, 3).map(error => `Row ${error.row ?? '?'}: ${error.message}`),
  }
}

function openOriginal(url?: string): void {
  if (url) window.open(url, '_blank', 'noopener,noreferrer')
}

async function loadTextPreview(url: string, signal: AbortSignal): Promise<string> {
  const response = await fetch(url, {signal, credentials: 'omit'})
  if (!response.ok) throw new Error(`Preview unavailable (${response.status})`)
  const declaredSize = Number(response.headers.get('Content-Length') || 0)
  if (declaredSize > MAX_TEXT_PREVIEW_BYTES) throw new Error('This file is too large for an in-chat preview. Open the original instead.')
  const text = await response.text()
  if (new Blob([text]).size > MAX_TEXT_PREVIEW_BYTES) throw new Error('This file is too large for an in-chat preview. Open the original instead.')
  return text
}

const TextPreview: React.FC<{kind: ReturnType<typeof attachmentPreviewKind>; text: string; name: string}> = ({kind, text, name}) => {
  const csv = useMemo(() => kind === 'csv' ? parseCsvPreview(text, name) : null, [kind, name, text])
  if (kind === 'markdown') {
    return <Box sx={{maxWidth: 960, mx: 'auto', '& table': {borderCollapse: 'collapse'}, '& th, & td': {border: 1, borderColor: 'divider', px: 1, py: 0.5}, '& img': {maxWidth: '100%'}}}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        skipHtml
        components={{
          // Do not automatically contact URLs embedded in an untrusted uploaded document.
          img: ({alt}) => <Typography component="span" color="text.secondary">[Image: {alt || 'preview omitted'}]</Typography>,
          a: ({href, children}) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
        }}
      >{text}</ReactMarkdown>
    </Box>
  }
  if (kind === 'json') {
    let formatted = text
    try { formatted = JSON.stringify(JSON.parse(text), null, 2) } catch { /* show malformed JSON as text */ }
    return <Box component="pre" sx={{m: 0, p: 2, borderRadius: 1, bgcolor: 'background.default', fontFamily: 'monospace', fontSize: '0.78rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{formatted}</Box>
  }
  if (csv) {
    if (!csv.rows.length) return <Alert severity="info">This file has no displayable rows.</Alert>
    const [head, ...body] = csv.rows
    return <>
      {csv.errors.length > 0 && <Alert severity="warning" sx={{mb: 1}}>{csv.errors.join(' · ')}</Alert>}
      <TableContainer sx={{border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 'calc(100vh - 260px)'}}>
        <Table stickyHeader size="small" aria-label={`${name} preview`}>
          <TableHead><TableRow>{head.map((cell, index) => <TableCell key={index} sx={{fontWeight: 700, whiteSpace: 'nowrap'}}>{cell || `Column ${index + 1}`}</TableCell>)}</TableRow></TableHead>
          <TableBody>{body.map((row, rowIndex) => <TableRow key={rowIndex}>{head.map((_, columnIndex) => <TableCell key={columnIndex} sx={{maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={row[columnIndex] ?? ''}>{row[columnIndex] ?? ''}</TableCell>)}</TableRow>)}</TableBody>
        </Table>
      </TableContainer>
      {(csv.truncatedRows || csv.truncatedColumns) && <Typography sx={{mt: 1, fontSize: '0.75rem', color: 'text.secondary'}}>Preview limited to {MAX_CSV_ROWS} rows and {MAX_CSV_COLUMNS} columns. Open the original for the complete file.</Typography>}
    </>
  }
  return <Box component="pre" sx={{m: 0, fontFamily: 'monospace', fontSize: '0.82rem', lineHeight: 1.65, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{text}</Box>
}

export const AttachmentPreviewDialog: React.FC<{attachment: ChatAttachment | null; onClose: () => void}> = ({attachment, onClose}) => {
  const kind = attachment ? attachmentPreviewKind(attachment) : 'download'
  const [text, setText] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const textKind = ['markdown', 'text', 'json', 'csv'].includes(kind)

  useEffect(() => {
    setText(null)
    setError(null)
    if (!attachment?.url || !textKind) return
    const controller = new AbortController()
    void loadTextPreview(attachment.url, controller.signal)
      .then(setText)
      .catch(reason => { if ((reason as Error).name !== 'AbortError') setError(reason instanceof Error ? reason.message : 'Preview unavailable') })
    return () => controller.abort()
  }, [attachment?.id, attachment?.url, textKind])

  return <Dialog open={Boolean(attachment)} onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="attachment-preview-title">
    {attachment && <>
      <DialogTitle id="attachment-preview-title" sx={{display: 'flex', alignItems: 'center', pr: 6}}>
        <Typography component="span" noWrap sx={{fontWeight: 600}} title={attachment.name}>{attachment.name}</Typography>
        <IconButton aria-label="Close attachment preview" onClick={onClose} sx={{position: 'absolute', right: 12, top: 10}}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{minHeight: 280, maxHeight: 'calc(100vh - 170px)', bgcolor: kind === 'image' ? 'action.hover' : 'background.paper'}}>
        {kind === 'image' && <Box component="img" src={attachment.url} alt={attachment.name} sx={{display: 'block', maxWidth: '100%', maxHeight: 'calc(100vh - 230px)', objectFit: 'contain', borderRadius: 1, mx: 'auto'}} />}
        {kind === 'pdf' && <Box component="iframe" src={attachment.url} title={attachment.name} sx={{display: 'block', width: '100%', height: 'calc(100vh - 230px)', minHeight: 520, border: 0, bgcolor: '#fff'}} />}
        {textKind && !text && !error && <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 240, gap: 1}}><CircularProgress size={20} /><Typography color="text.secondary">Loading preview…</Typography></Box>}
        {error && <Alert severity="warning">{error}</Alert>}
        {text !== null && <TextPreview kind={kind} text={text} name={attachment.name} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" endIcon={<OpenInNewIcon />} onClick={() => openOriginal(attachment.url)}>Open original</Button>
      </DialogActions>
    </>}
  </Dialog>
}
